import { faEdit, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TFunction } from 'i18next'
import React, { FC, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AppContext from '../../../gateway/Authentication/AppContext'
import Authorization from '../../../gateway/Authentication/Authorization'
import AuthService from '../../../gateway/Authentication/AuthService'
import DialogContext from '../../../gateway/Authentication/DialogContext'
import { ConfirmDialogProps } from '../../../gateway/components/ConfirmDialog'
import DataTable from '../../../gateway/components/DataTable'
import { EditDialogProps } from '../../../gateway/components/EditDialog'
import ProtectedButton from '../../../gateway/components/ProtectedButton'
import Loading from '../../../gateway/layout/Loading'
import { expandRowFunction, formatBooleanColumn } from '../../../gateway/utils/DatatableUtil'
import WebDAVService from '../service/WebDAVService'

const CsvConfiguration: FC<{}> = () => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const { userPermissions } = useContext(AppContext)
  const [webdavConfigurations, setWebdavConfigurations] = useState([] as any[])
  const { confirmDialogPropsSetter, editDialogPropsSetter } = useContext(DialogContext)

  useEffect(() => {
    async function loadData() {
      await WebDAVService.getAllConfigurations(setWebdavConfigurations)
    }

    loadData().finally(() => setLoading(false))
  }, [])

  async function callFunctionAfterFetch(
    func: (t: TFunction, editDialogPropsSetter: any, policies: any[], setWebdavConfigurations: React.Dispatch<any>, row?: any) => any,
    row?: any
  ) {
    let policies = await WebDAVService.getAllWebDAVpolicies().then((policies) =>
      policies
        .sort((a: { configName: string }, b: { configName: any }) => a.configName.localeCompare(b.configName))
        .filter((policy: { configs: any[] }) => !policy.configs.find((config) => config.method === 'StaticReplacement'))
    )

    if (row) func(t, editDialogPropsSetter, policies, setWebdavConfigurations, row)
    else func(t, editDialogPropsSetter, policies, setWebdavConfigurations)
  }

  const columns = [
    {
      text: t('webdav.name'),
      dataField: 'name',
      sort: true,
      classes: 'position-relative',
    },
    {
      text: t('webdav.pattern'),
      dataField: 'pattern',
      sort: true,
      classes: 'position-relative',
    },
    {
      text: t('webdav.policyName'),
      dataField: 'policyName',
      sort: true,
      classes: 'position-relative',
    },
    {
      hidden: !AuthService.hasPermission(userPermissions, Authorization.adapter.edit),
      dataField: 'df1',
      isDummyField: true,
      text: t('action'),
      headerAlign: 'center',
      formatter: (cellData: any, row: any) => {
        return (
          <>
            <ProtectedButton
              variant='link'
              className='text-decoration-none pr-2'
              tooltip={t('edit')}
              onClick={() => callFunctionAfterFetch(editWebdavConfiguration, row)}>
              <FontAwesomeIcon icon={faEdit} />
            </ProtectedButton>
            <ProtectedButton
              variant='link'
              className='text-danger text-link text-decoration-none'
              tooltip={t('delete')}
              onClick={() => deleteWebdavConfiguration(t, confirmDialogPropsSetter, setWebdavConfigurations, row)}>
              <FontAwesomeIcon icon={faTrashAlt} />
            </ProtectedButton>
          </>
        )
      },
    },
  ]

  const expandRow = expandRowFunction(renderer, () => { }, t)

  return (
    <Loading isLoading={loading}>
      <DataTable
        data={webdavConfigurations}
        columns={columns}
        caption={t('webdav.configurationHeader')}
        captionButtons={[
          {
            onClick: () => callFunctionAfterFetch(createWebdavConfiguration),
            text: t('new'),
            permissions: Authorization.adapter.edit,
          },
        ]}
        searchPlaceholder={t('webdav.searchPlaceholder')}
        expandRow={expandRow}
      />
    </Loading>
  )
}

function renderer(row: any, rowIndex: any, ...args: any[]) {
  //const data: any[] = [row]
  const t: TFunction = args[0]

  const columns: any[] = [
    {
      text: t('attribute'),
      dataField: 'attribute',
      classes: 'col-sm-2',
      sort: true,
    },
    {
      text: t('value'),
      dataField: 'value',
      classes: 'col-sm-10',
      formatter: (cell: any, row: any) => {
        if (row.type === 'BOOLEAN') {
          return formatBooleanColumn(cell)
        }
        return cell
      },
      sort: true,
    },
  ]

  const data: any[] = [
    {
      attribute: t('webdav.fieldSeparator'),
      value: row.fieldSeparator,
      type: 'STRING',
    },
    {
      attribute: t('webdav.dataSeparator'),
      value: row.dataSeparator,
      type: 'STRING',
    },
    {
      attribute: t('webdav.defaultEncoding'),
      value: row.defaultEncoding,
      type: 'STRING',
    },
    {
      attribute: t('webdav.header'),
      value: row.header,
      type: 'STRING',
    },
    {
      attribute: t('webdav.ignoreBefore'),
      value: row.ignoreBefore,
      type: 'BOOLEAN',
    },
    {
      attribute: t('webdav.ignoreLast'),
      value: row.ignoreLast,
      type: 'STRING',
    },
  ]

  if (!data.length) {
    return <React.Fragment key={row.id} />
  }
  return (
    <React.Fragment key={row.id}>
      <DataTable
        data={data}
        columns={columns}
        searchable={false}
        striped={false}
        hover={false}
        condensed={true}
        headerClasses='d-none'
        bodyClasses='container tr-row'
      />
    </React.Fragment>
  )
}

function createWebdavConfiguration(t: TFunction, editDialogPropsSetter: any, policies: any[], setWebdavConfigurations: React.Dispatch<any>) {
  const sortedPolicies = policies.sort((a, b) => a.configName.localeCompare(b.configName))
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('webdav.createHeader'),
    onSubmit: WebDAVService.createConfiguration,
    onSubmitExtraArgs: [t, setWebdavConfigurations],
    data: [
      {
        id: 'name',
        type: 'text',
        label: t('webdav.name'),
        placeholder: t('webdav.namePlaceholder'),
        required: true,
      },
      {
        id: 'pattern',
        type: 'text',
        label: t('webdav.pattern'),
        placeholder: t('webdav.patternPlaceholder'),
        required: true,
      },
      {
        id: 'policyId',
        type: 'select',
        options: sortedPolicies.map((policy) => ({ label: policy.configName, value: policy.id })),
        value: sortedPolicies[0].id,
        label: t('webdav.policyName'),
        required: true,
      },
      {
        id: 'fieldSeparator',
        type: 'text',
        label: t('webdav.fieldSeparator'),
        placeholder: t('webdav.fieldSeparatorPlaceholder'),
      },
      {
        id: 'dataSeparator',
        type: 'text',
        label: t('webdav.dataSeparator'),
        placeholder: t('webdav.dataSeparatorPlaceholder'),
      },
      {
        id: 'defaultEncoding',
        type: 'text',
        label: t('webdav.defaultEncoding'),
        placeholder: t('webdav.defaultEncodingPlaceholder'),
      },
      {
        id: 'columns',
        type: 'text',
        label: t('webdav.columns'),
        render: false,
      },
      {
        id: 'header',
        type: 'number',
        label: t('webdav.header'),
        placeholder: t('webdav.headerPlaceholder'),
      },
      {
        groupLabel: t('webdav.ignoreBefore'),
        groupName: 'ignoreBefore',
        type: 'switch',
        checkboxes: [
          {
            id: 'ignoreBefore',
            value: false,
          },
        ],
      },
      {
        id: 'ignoreLast',
        type: 'number',
        label: t('webdav.ignoreLast'),
        placeholder: t('webdav.ignoreLastPlaceholder'),
      },
    ],
    labelGridSize: 3,
  }
  editDialogPropsSetter(editDialogProps)
}

function editWebdavConfiguration(t: TFunction, editDialogPropsSetter: any, policies: any[], setWebdavConfigurations: React.Dispatch<any>, row: any) {
  const sortedPolicies = policies.sort((a, b) => a.configName.localeCompare(b.configName))
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('webdav.editHeader'),
    onSubmit: WebDAVService.editConfiguration,
    onSubmitExtraArgs: [t, setWebdavConfigurations],
    data: [
      {
        id: 'id',
        type: 'hidden',
        value: row.id,
        render: false,
      },
      {
        id: 'name',
        type: 'text',
        label: t('webdav.name'),
        value: row.name,
        required: true,
        placeholder: t('webdav.namePlaceholder'),
      },
      {
        id: 'pattern',
        type: 'text',
        label: t('webdav.pattern'),
        value: row.pattern,
        required: true,
        placeholder: t('webdav.patternPlaceholder'),
      },
      {
        id: 'policyId',
        type: 'select',
        options: sortedPolicies.map((policy) => ({ label: policy.configName, value: policy.id })),
        value: row.policyId,
        label: t('webdav.policyName'),
        required: true,
      },
      {
        id: 'fieldSeparator',
        type: 'text',
        label: t('webdav.fieldSeparator'),
        placeholder: t('webdav.fieldSeparatorPlaceholder'),
        value: row.fieldSeparator,
      },
      {
        id: 'dataSeparator',
        type: 'text',
        label: t('webdav.dataSeparator'),
        placeholder: t('webdav.dataSeparatorPlaceholder'),
        value: row.dataSeparator,
      },
      {
        id: 'defaultEncoding',
        type: 'text',
        value: row.defaultEncoding,
        placeholder: t('webdav.defaultEncodingPlaceholder'),
        label: t('webdav.defaultEncoding'),
      },
      {
        id: 'columns',
        type: 'text',
        value: row.columns,
        render: false,
        label: t('webdav.columns'),
      },
      {
        id: 'header',
        type: 'text',
        value: row.header,
        placeholder: t('webdav.headerPlaceholder'),
        label: t('webdav.header'),
      },
      {
        groupLabel: t('webdav.ignoreBefore'),
        groupName: 'ignoreBefore',
        type: 'switch',
        checkboxes: [
          {
            id: 'ignoreBefore',
            value: row.ignoreBefore,
          },
        ],
      },
      {
        id: 'ignoreLast',
        type: 'text',
        value: row.ignoreLast,
        placeholder: t('webdav.ignoreLastPlaceholder'),
        label: t('webdav.ignoreLast'),
      },
    ],
    labelGridSize: 3,
  }
  editDialogPropsSetter(editDialogProps)
}

function deleteWebdavConfiguration(t: Function, confirmDialogPropsSetter: any, setWebdavConfigurations: React.Dispatch<any>, row: any) {
  const deleteDialogProps: ConfirmDialogProps = {
    show: true,
    header: t('webdav.deleteHeader'),
    body: t('webdav.deleteBody', { name: row.name }),
    submitText: t('yes'),
    onSubmit: WebDAVService.deleteConfiguration,
    onSubmitExtraArgs: [row, t, setWebdavConfigurations],
    cancelText: t('no'),
  }
  confirmDialogPropsSetter(deleteDialogProps)
}

export default CsvConfiguration
